/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useCallback, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import {
  EuiButtonEmpty,
  EuiButtonIcon,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormHelpText,
  EuiPanel,
  EuiPopover,
  EuiSpacer,
  EuiTitle,
  IconType,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { LinkInfoAppKey } from '@modules/ui-types'
import { CuiRouterLink } from '@modules/cui/RouterButton'

import {
  EndpointsPopover,
  EndpointsPopoverItem,
  EndpointsOpenButtonProps,
} from './EndpointsPopover'

const iconsByAppKey: Partial<Record<LinkInfoAppKey, IconType>> = {
  elasticsearch: 'logoElasticsearch',
  kibana: 'logoKibana',
  integrations_server: 'apmApp',
  apm: 'apmApp',
  fleet: 'fleetApp',
  appsearch: 'logoAppSearch',
  ingest: 'logoElastic',
  enterprise_search: 'logoEnterpriseSearch',
}

const messages = defineMessages({
  privateEndpoint: {
    id: 'endpoints-panel.privateEndpoint',
    defaultMessage: 'Private endpoint',
  },
  publicEndpoint: {
    id: 'endpoints-panel.publicEndpoint',
    defaultMessage: 'Public endpoint',
  },
  clusterId: {
    id: 'endpoints-panel.clusterId',
    defaultMessage: 'Cluster ID',
  },
  componentId: {
    id: 'endpoints-panel.componentId',
    defaultMessage: 'Component ID',
  },
  openApp: {
    id: 'endpoints-panel.openApp',
    defaultMessage: 'Open {appLabel}',
  },
  endpointsToolTip: {
    id: 'endpoints-panel.toolTip',
    defaultMessage:
      "To log in to the application you will need to have your deployment's credentials from when it was created.",
  },
  endpointsPanelToolTipResetPasswordLink: {
    id: 'endpoints-panel.toolTipResetPasswordLink',
    defaultMessage: "Reset your password if you've lost it.",
  },
})

interface EndpointsPanelToolTipProps {
  resetPasswordUrl?: string
}

const EndpointsPanelToolTip = (props: EndpointsPanelToolTipProps) => {
  const { resetPasswordUrl } = props
  const { formatMessage } = useIntl()

  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false)

  const openPopover = useCallback(() => {
    setIsPopoverOpen((prev) => !prev)
  }, [])

  const closePopover = useCallback(() => {
    setIsPopoverOpen(false)
  }, [])

  const button = (
    <EuiButtonIcon
      data-test-id='endpoints-panel.tooltip-button'
      onClick={openPopover}
      iconType='question'
      color='text'
      aria-label='Endpoints panel tooltip'
    />
  )

  return (
    <EuiPopover
      button={button}
      panelStyle={{ width: '15rem' }}
      panelPaddingSize='m'
      isOpen={isPopoverOpen}
      anchorPosition='rightCenter'
      closePopover={closePopover}
    >
      <EuiFormHelpText data-test-id='endpoints-panel.tooltip-content'>
        {resetPasswordUrl ? (
          <React.Fragment>
            {formatMessage(messages.endpointsToolTip)}

            <span>{` `}</span>

            <CuiRouterLink to={resetPasswordUrl}>
              {formatMessage(messages.endpointsPanelToolTipResetPasswordLink)}
            </CuiRouterLink>
          </React.Fragment>
        ) : (
          formatMessage(messages.endpointsToolTip)
        )}
      </EuiFormHelpText>
    </EuiPopover>
  )
}

export interface EndpointsPanelLink {
  isEnabled: boolean
  showOpenButton: boolean
  appType: LinkInfoAppKey
  label: string
  url?: string
  privateUrl?: string
  openUrl?: string
  id?: string
  buttonDataTestId?: string
}

interface EndpointsPanelProps {
  links: EndpointsPanelLink[]
  resetPasswordUrl?: string
}

export const EndpointsPanel = (props: EndpointsPanelProps) => {
  const { links, resetPasswordUrl } = props
  const [openPopover, setOpenPopover] = useState<LinkInfoAppKey | null>(null)

  const { formatMessage } = useIntl()

  const getLinkPopoverItems = (link: EndpointsPanelLink): EndpointsPopoverItem[] => {
    const items: EndpointsPopoverItem[] = []

    if (link.isEnabled && link.url) {
      items.push({
        title: formatMessage(messages.publicEndpoint),
        value: link.url!,
      })
    }

    if (link.isEnabled && link.privateUrl) {
      items.push({
        title: formatMessage(messages.privateEndpoint),
        value: link.privateUrl!,
      })
    }

    if (link.id) {
      items.push({
        title: formatMessage(
          link.appType === 'elasticsearch' ? messages.clusterId : messages.componentId,
        ),
        value: link.id,
      })
    }

    return items
  }

  const getButtonProps = (link: EndpointsPanelLink): EndpointsOpenButtonProps | undefined => {
    if (link.showOpenButton && link.openUrl) {
      return {
        title: formatMessage(messages.openApp, { appLabel: link.label }),
        url: link.openUrl,
      }
    }

    return undefined
  }

  const onPopoverClose = () => {
    setOpenPopover(null)
  }

  const onButtonClick = (type: LinkInfoAppKey) => {
    setOpenPopover((prevType) => (prevType === type ? null : type))
  }

  const renderPopoverButtons = () =>
    links.map((link, index) => {
      if (!link) {
        return null
      }

      const popoverItems = getLinkPopoverItems(link)

      const buttonProps = getButtonProps(link)

      const icon = iconsByAppKey[link.appType]!
      return (
        <EuiFlexItem
          key={index}
          grow={false}
          data-test-id={link.buttonDataTestId ?? `endpoints-panel.link-${link.appType}`}
        >
          <EndpointsPopover
            iconType={icon}
            title={link.label}
            popoverItems={popoverItems}
            anchorButton={
              <EuiButtonEmpty
                aria-label={`Open ${link.label}`}
                size='s'
                iconType={icon}
                disabled={!link.isEnabled}
                onClick={() => onButtonClick(link.appType)}
              >
                {link.label}
              </EuiButtonEmpty>
            }
            openButtonProps={buttonProps}
            isOpen={openPopover === link.appType}
            closePopover={onPopoverClose}
          />
        </EuiFlexItem>
      )
    })

  return (
    <EuiPanel hasBorder={true} data-test-id='endpoints-panel'>
      <EuiFlexGroup direction='column' gutterSize='none' style={{ height: '100%' }}>
        {/* Title block */}
        <EuiFlexItem grow={false}>
          <EuiFlexGroup direction='row' alignItems='center' gutterSize='xs'>
            <EuiFlexItem grow={false}>
              <EuiTitle size='xs' data-test-id='endpoints-panel.title'>
                <h3>
                  <FormattedMessage
                    id='endpoints-panel.title'
                    defaultMessage='Application endpoints, cluster and component IDs'
                  />
                </h3>
              </EuiTitle>
            </EuiFlexItem>
            {resetPasswordUrl && (
              <EuiFlexItem grow={false}>
                <EndpointsPanelToolTip resetPasswordUrl={resetPasswordUrl} />
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
        </EuiFlexItem>
        <EuiSpacer size='xs' />

        {/* Endpoints button block */}
        <EuiFlexItem>
          <EuiFlexGroup direction='row' responsive={false} wrap={true} alignItems='center'>
            {renderPopoverButtons()}
          </EuiFlexGroup>
        </EuiFlexItem>
      </EuiFlexGroup>
    </EuiPanel>
  )
}
